// v2 horse — a real horse build: long legs with hooves joined at the hip, a
// deep chest and rounded croup on the barrel, a strongly arched neck carrying a
// refined down-angled head with a tapered muzzle and pricked ears, a mane crest
// flopped to one side with a forelock, and a high-set tail sweeping back and
// down into a full plume. Three coats: chestnut, bay (black points) and dapple
// grey. Ported 1:1 from age-of-mcp's v2/horse.ts, using the library's shared
// `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { coat: "#9a6234", shade: "#86532a", mane: "#3a2a18", muzzle: "#6e4a28", leg: "#7a4e26", hoof: "#2e2620" },
  { coat: "#6e4526", shade: "#5e3a20", mane: "#1f1813", muzzle: "#4a3018", leg: "#241c14", hoof: "#16120e" },
  { coat: "#cfcac0", shade: "#b8b2a6", mane: "#8e8880", muzzle: "#8a847a", leg: "#a8a196", hoof: "#48423a" },
];

// The neck's lean from vertical — the mane crest shares it so they stay
// parallel.
const NECK_LEAN = 0.62;

function horseParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.078, dz: 0.03, bodyY: 0.158, rTop: 0.0085, rFoot: 0.006,
    color: c.leg, hoof: { color: c.hoof, r: 0.0085, h: 0.015 },
  });
  parts.push(
    // barrel with a deep chest and rounded croup
    { geometry: new THREE.IcosahedronGeometry(0.058, 0), color: c.coat,
      position: [-0.002, 0.158, 0], scale: [1.7, 0.95, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.shade,
      position: [0.07, 0.152, 0], scale: [1.0, 1.05, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.coat,
      position: [-0.075, 0.165, 0], scale: [1.05, 0.95, 0.85] },
    // the arched neck rising steeply to the poll
    { geometry: new THREE.CylinderGeometry(0.018, 0.03, 0.105, 5), color: c.coat,
      position: [0.105, 0.205, 0], rotation: [0, 0, -NECK_LEAN] },
    // mane crest along the neck's back edge, flopped a touch to one side
    { geometry: new THREE.BoxGeometry(0.014, 0.115, 0.013), color: c.mane,
      position: [0.0855, 0.219, 0.005], rotation: [0, 0, -NECK_LEAN] },
    // refined head angled down-forward, tapered muzzle, forelock
    { geometry: new THREE.BoxGeometry(0.05, 0.03, 0.026), color: c.coat,
      position: [0.156, 0.252, 0], rotation: [0, 0, -0.35] },
    { geometry: new THREE.ConeGeometry(0.0115, 0.032, 5), color: c.muzzle,
      position: [0.185, 0.243, 0], rotation: [0, 0, -Math.PI / 2 - 0.35] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.mane,
      position: [0.162, 0.272, 0] },
    // pricked ears
    { geometry: new THREE.ConeGeometry(0.0055, 0.018, 4), color: c.shade,
      position: [0.147, 0.272, 0.011], rotation: [0.15, 0, 0.1] },
    { geometry: new THREE.ConeGeometry(0.0055, 0.018, 4), color: c.shade,
      position: [0.147, 0.272, -0.011], rotation: [-0.15, 0, 0.1] },
    // eyes
    { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#1c140e",
      position: [0.155, 0.26, 0.0145] },
    { geometry: new THREE.IcosahedronGeometry(0.0036, 0), color: "#1c140e",
      position: [0.155, 0.26, -0.0145] },
    // high-set tail sweeping back-down into a full plume
    { geometry: new THREE.CylinderGeometry(0.0085, 0.0065, 0.05, 4), color: c.mane,
      position: [-0.127, 0.1535, 0], rotation: [0, 0, 2.29] },
    { geometry: new THREE.IcosahedronGeometry(0.016, 0), color: c.mane,
      position: [-0.148, 0.112, 0], scale: [1.2, 1.9, 0.7] },
  );
  return parts;
}

export const horseV2 = defineModel({
  id: "horse-v2",
  scale: 2.3, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Horse (v2)",
  tags: ["creature","horse","low-poly","age-of-mcp","fable"],
  summary: "Low-poly horse.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.16,
  fits: ["draft"],
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => horseParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => horseParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => horseParts(2) },
  ],
  animations: [],
});
